import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { hasInAppHistory } from '../hooks/useSmartBack';
import './AppHeader.css';

/**
 * AppHeader - The ONE shared top bar for every page that has a back button.
 *
 * Rules enforced by this component (do NOT override from pages):
 *   - Material-style layout: back button at the inline-start, title aligned
 *     next to it (right-aligned in RTL, left-aligned in LTR). Titles never
 *     center — consistency across the app.
 *   - Flex layout (never absolute positioning). Back button, title block
 *     and right slot live as separate flex children so they can never
 *     overlap regardless of content length.
 *   - Sticky by default (top: 0, z-index 100) so content always scrolls
 *     underneath it.
 *   - Safe-area aware: respects env(safe-area-inset-top) on notched devices.
 *   - RTL aware: back arrow auto-flips and the whole row mirrors via the
 *     parent dir attribute.
 *
 * Props:
 *   title       – string, required unless `titleSlot` is provided.
 *   subtitle    – optional short sub-line (single line, ellipsized).
 *   titleSlot   – optional React node that replaces the title block entirely.
 *   onBack      – optional click handler. If both onBack and backTo are null
 *                 the component falls back to navigate(-1). Pass
 *                 `showBack={false}` to hide the back button (for root/tab
 *                 pages such as Messages inbox).
 *   backTo      – optional route string passed to navigate().
 *   backFallback– optional route used ONLY when there is no in-app history
 *                 (deep link / fresh load). Defaults to '/'. With history
 *                 present the header always pops (navigate(-1)) so the real
 *                 back-stack and page state are preserved.
 *   rightSlot   – optional React node rendered at the inline-end (coin pill,
 *                 actions, etc). Safe from overlap.
 *   variant     – 'default' (solid surface) | 'gradient' (Wassla green) |
 *                 'indigo' (CV Builder gradient) | 'transparent'.
 *   sticky      – default true.
 *   showBack    – default true.
 */
export default function AppHeader({
  title,
  subtitle,
  titleSlot,
  onBack,
  backTo,
  backFallback,
  rightSlot,
  variant = 'default',
  sticky = true,
  showBack = true,
  className = '',
  testId = 'app-header',
}) {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const isRTL =
    i18n.language?.startsWith('ar') ||
    (typeof document !== 'undefined' && document.documentElement?.dir === 'rtl');

  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  const handleBack = () => {
    if (onBack) return onBack();
    if (backTo) return navigate(backTo);
    if (hasInAppHistory()) return navigate(-1);
    navigate(backFallback || '/', { replace: true });
  };

  return (
    <header
      className={[
        'app-header',
        `app-header--${variant}`,
        sticky ? 'app-header--sticky' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid={testId}
    >
      <div className="app-header__inner">
        {showBack ? (
          <button
            type="button"
            className="app-header__back"
            onClick={handleBack}
            aria-label={t('bookdeta.back', { defaultValue: 'Back' })}
            data-testid="app-header-back-btn"
          >
            <BackIcon size={22} strokeWidth={2.25} />
          </button>
        ) : (
          <span className="app-header__back-placeholder" aria-hidden="true" />
        )}

        <div className="app-header__titles">
          {titleSlot
            ? titleSlot
            : (
              <>
                {title ? (
                  <h1 className="app-header__title" data-testid="app-header-title">
                    {title}
                  </h1>
                ) : null}
                {subtitle ? (
                  <p className="app-header__subtitle" data-testid="app-header-subtitle">
                    {subtitle}
                  </p>
                ) : null}
              </>
            )}
        </div>

        {rightSlot ? (
          <div className="app-header__right" data-testid="app-header-right">
            {rightSlot}
          </div>
        ) : null}
      </div>
    </header>
  );
}
